import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { PaymentMethodsEnum } from '@ctct/domain-layer';
import { envs } from '@ctct/infrastructure-layer';
import { Button, Tabs } from '@ctct/payflow-ui';
import { Alert } from '@ctct/presentation-layer';

import applepayIcon from './assets/applepay.svg';
import amazonPayIcon from './assets/v2-amazon.svg';
import creditCardIcon from './assets/v2-creditcard.svg';
import echeckIcon from './assets/v2-echeck.svg';
import paypalIcon from './assets/v2-paypal.svg';
import type { PaymentMethodsLoadedProps } from './types';
import { AmazonPay, CreditCard, ECheck, PayPal, ApplePay } from '../../components';
import './payment-methods-widget-content.scss';
import { useIsMobile } from '../../hooks';

export const PaymentMethodsWidgetContent = ({
  data,
  handlers,
  hooks,
  isApplePayEnabled,
  disableApplePayAsNewPaymentMethod,
}: PaymentMethodsLoadedProps) => {
  const { t } = useTranslation();

  const selectedMethod = hooks.useSelectedMethod();
  const applePayConnected = hooks.useApplePayConnected();
  const tabsLoading = hooks.useTabsLoading();
  const isFullLoading = hooks.useFullLoading();
  const currentPaymentMethodWarning = hooks.useCurrentPaymentMethodWarning();
  const currentPaymentMethod = data.getCurrentPaymentMethod();

  const { isMobile } = useIsMobile();
  const isSecureDomain = envs.isSecure();

  const isApplePaySupported = data.getIsApplePaySupportedInBrowser();
  const shouldShowMobileApplePayButton =
    !disableApplePayAsNewPaymentMethod && isMobile && isApplePaySupported && isApplePayEnabled;

  const shouldHideApplePayTab =
    (!isApplePaySupported && !(currentPaymentMethod && data.isApplePay(currentPaymentMethod))) ||
    disableApplePayAsNewPaymentMethod;

  const getTag = useCallback(
    (method: typeof selectedMethod) => {
      if (data.isAmazonPay(method)) {
        return (
          <img
            src={amazonPayIcon}
            alt={t('payment-methods.amazon-pay')}
            data-qe-id="amazon-pay-tab"
            className="tab-item-img amazonpay"
          />
        );
      }

      if (data.isCreditCard(method)) {
        return (
          <div className="payment-label-v2">
            <img src={creditCardIcon} alt={t('payment-methods.cc')} data-qe-id="cc-tab" className="tab-item-img" />
            {t('payment-methods.cc')}
          </div>
        );
      }

      if (data.isPayPal(method)) {
        return (
          <img src={paypalIcon} alt={t('payment-methods.paypal')} data-qe-id="paypal-tab" className="tab-item-img" />
        );
      }

      if (data.isEcheck(method)) {
        return (
          <div className="payment-label-v2">
            <img src={echeckIcon} alt={t('payment-methods.echeck')} data-qe-id="echeck-tab" className="tab-item-img" />
            {t('payment-methods.echeck')}
          </div>
        );
      }

      if (data.isApplePay(method)) {
        return (
          <div className="payment-label-v2">
            {t('payment-methods.apple_pay_pay_with')}
            <img
              src={applepayIcon}
              alt={t('payment-methods.apple_pay')}
              data-qe-id="applepay-tab"
              className="tab-item-img"
            />
          </div>
        );
      }

      return method;
    },
    [data, t]
  );

  const getContent = useCallback(
    (method: typeof selectedMethod) => {
      if (data.isAmazonPay(method)) {
        return <AmazonPay data={data} handlers={handlers} hooks={hooks} />;
      }

      if (data.isCreditCard(method)) {
        return <CreditCard data={data} handlers={handlers} hooks={hooks} />;
      }

      if (data.isPayPal(method)) {
        return <PayPal data={data} handlers={handlers} hooks={hooks} showBuyNowButton />;
      }

      if (data.isEcheck(method)) {
        return <ECheck data={data} handlers={handlers} hooks={hooks} />;
      }

      if (data.isApplePay(method)) {
        return <ApplePay data={data} handlers={handlers} hooks={hooks} />;
      }

      return method;
    },
    [data, handlers, hooks]
  );

  // if apple pay is connected, we do not want to show payment methods tabs [by design]
  //  instead we show apple pay info + edit button
  // Clicking edit button disconnects apple pay as payment method and allows selecting any other payment method
  if (applePayConnected && data.isApplePay()) {
    return (
      <>
        {!isApplePaySupported && (
          <Alert message={t('payment-methods.apple_pay_switch_to_safari_to_update_payment_method')} />
        )}
        {currentPaymentMethodWarning && <Alert message={currentPaymentMethodWarning} />}

        <div className="apple-pay-card-info-wrapper-with-edit-button">
          <ApplePay data={data} handlers={handlers} hooks={hooks} />
          <Button
            variant="text"
            className="edit-applepay-button"
            data-qe-id="edit-applepay"
            onClick={() => {
              handlers.onApplePayEditClick();

              if (disableApplePayAsNewPaymentMethod) {
                // on billing info page, when switching payment method from existing apple pay, we want to switch to credit card tab, because apple pay is not selectable
                handlers.onPaymentMethodSelect(PaymentMethodsEnum.CREDIT_CARD);
              }
            }}
            busy={tabsLoading || isFullLoading}
            disabled={tabsLoading || isFullLoading}
          >
            {t('payment-methods.edit')}
          </Button>
        </div>
      </>
    );
  }

  return (
    <div className="payment-methods-widget-container v2">
      {shouldShowMobileApplePayButton && (
        <div className="apple-pay-mobile-button-wrapper">
          <ApplePay data={data} handlers={handlers} hooks={hooks} />
          <div className="apple-pay-alternatives-header">
            <span>{t('payment-methods.apple_pay_alternatives_header')}</span>
          </div>
        </div>
      )}
      <div className="tab-selector-container">
        <Tabs
          disableTabs={tabsLoading || isFullLoading}
          disableContent={isFullLoading}
          variant="button-tabs"
          tabs={data
            .getPaymentMethods()
            .filter((method) => !(data.isApplePay(method) && (isMobile || shouldHideApplePayTab)))
            .map((method) => ({
              id: method,
              title: getTag(method),
              content: (
                <>
                  {currentPaymentMethodWarning && currentPaymentMethod === method && !data.isApplePay(method) && (
                    <Alert message={currentPaymentMethodWarning} />
                  )}
                  {getContent(method)}
                </>
              ),
            }))}
          value={selectedMethod}
          onValueChange={(val) => handlers.onPaymentMethodSelect(val, isMobile)}
        />
      </div>
    </div>
  );
};
